<script setup lang="ts">
import {ref, computed} from "vue";
import {
  kdm,
  drawerVisible,
  editMode,
  addAxis,
  deleteAxis,
  loadKdm,
  openAxisRecycle,
} from '@/store/kdm';
import KDMCell from "@/kdm/KDMCell.vue";
import {Axis} from "#/KrrNode";
import KDMTableHeaderRow from "@/kdm/KDMTableHeaderRow.vue";
import KdmEditableAxisLabel from "@/kdm/KdmEditableAxisLabel.vue";

defineProps<{
  tab: Axis,
}>()

const addingRow = ref(false)
const newRowTitle = ref('')

const canManageAxis = computed(() => {
  const cu = kdm.value?.current_user;
  return cu?.is_admin || cu?.is_owner || cu?.is_coordinator || cu?.is_editor;
});

// All KDM types can add a row: types 1/2 directly, type 3 cascades to the
// linked KRR (the KDM inherits it).
const canAddRow = computed(() => !!kdm.value);

// Per-type explanation shown in the info tooltip next to the matrix type label.
const typeTooltip = computed(() => {
  switch (Number(kdm.value?.kdm_type)) {
    case 1:
      return 'Structure comes directly from its Worksite. No arguments are used — one Evaluation Value per cell.';
    case 2:
      return "Structure and arguments come directly from its Worksite. Arguments are child nodes of the Worksite's KIK.";
    case 3:
      return "Structure and arguments come from its Worksite, filtered through its KRR. Arguments are child nodes of the Worksite's KIK.";
    default:
      return '';
  }
});

async function handleAddRow() {
  if (!newRowTitle.value.trim()) return
  addingRow.value = true
  const success = await addAxis('row', newRowTitle.value.trim())
  if (success) newRowTitle.value = ''
  addingRow.value = false
}

async function handleDeleteRow(crossLinkId: string, title: string) {
  if (!confirm(`Supprimer la ligne "${title}" ?`)) return
  const success = await deleteAxis('row', crossLinkId, false)
  if (success && kdm.value?.nid) {
    await loadKdm(String(kdm.value.nid))
  }
}
</script>

<template>
  <table class="epb-matrix matrix-theme-evaluation" v-if="kdm">
    <thead>
    <KDMTableHeaderRow/>
    </thead>
    <tbody>
    <template v-for="(row,i) in kdm.rows" :key="row.uuid">
      <tr>
        <td class="sticky-column" :title="row.is_in_kik === false ? 'Indicator/Argument no longer contextualized in the KIK' : ''">
          <div class="row-header">
            <KdmEditableAxisLabel
              :axis="row"
              :can-edit="editMode && canManageAxis"
              :link-class="[row.moderation_class, row.is_in_kik === false ? 'fw-bold' : '']"
              icon-stroke="#212529"
            >
              <template #actions>
                <button
                  v-if="editMode && canManageAxis"
                  type="button"
                  class="btn-delete-row"
                  @click.stop="handleDeleteRow(row.crossLinkId, row.title)"
                  title="Delete"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </template>
            </KdmEditableAxisLabel>
          </div>
        </td>
        <template v-for="column in kdm.columns" :key="`${tab.id}-${row.id}-${column.id}`">
          <KDMCell :rid="row.id" :cid="column.id" :tid="tab.id"/>
        </template>
      </tr>
    </template>
    <tr v-if="editMode && canManageAxis" class="add-row-tr">
      <td class="sticky-column add-axis-cell">
        <div class="d-flex align-items-center gap-2">
          <template v-if="canAddRow">
            <input
              v-model="newRowTitle"
              type="text"
              class="form-control form-control-sm"
              placeholder="New QPI..."
              @keydown.enter="handleAddRow"
              :disabled="addingRow"
            />
            <button
              class="btn btn-sm btn-outline-primary"
              @click="handleAddRow"
              :disabled="addingRow || !newRowTitle.trim()"
            >
              <i class="bi bi-plus-lg"></i>
            </button>
          </template>
          <button
            type="button"
            class="btn btn-sm btn-success"
            style="background-color: #198754; border-color: #198754; color: white; font-weight: bold;"
            @click="openAxisRecycle('row')"
            title="Recycle existing value"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </td>
      <td :colspan="kdm.columns.length"></td>
    </tr>
    </tbody>
    <tfoot v-if="kdm.kdm_type || kdm.cell_weight_algorithm_label || kdm.max_arguments_per_cell">
      <tr>
        <td :colspan="kdm.columns.length + 1" class="algorithm-label">
          <small v-if="kdm.cell_weight_algorithm_label" class="text-muted d-block">
            <i class="bi bi-calculator me-1"></i>{{ kdm.cell_weight_algorithm_label }}
          </small>
          <small v-if="kdm.max_arguments_per_cell" class="text-muted d-block">
            <i class="bi bi-stack me-1"></i>Max {{ kdm.max_arguments_per_cell }} arguments per cell
          </small>
          <small v-if="kdm.kdm_type" class="text-muted d-block">
            <i class="bi bi-grid-3x3-gap me-1"></i>Kerbabel Deliberation Matrix Type {{ kdm.kdm_type }}
            <i class="bi bi-info-circle ms-1 kdm-type-info" :title="typeTooltip"></i>
          </small>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<style scoped>
.epb-matrix {
  margin-top: 1rem;
  table-layout: fixed;
}
.sticky-column {
  background-color: #e9ecef;
  color: #212529;
}
.sticky-column a {
  color: #212529;
  font-weight: 600;
  text-decoration: none;
}
tr:hover .sticky-column a {
  color: #212529;
  text-decoration: underline dotted;
}
td:first-child {
  text-align: right;
  width: 150px;
}
tr:nth-child(even) td:not(.sticky-column) {
  background-color: whitesmoke;
}
.add-row-tr td {
  background-color: transparent !important;
  border: none;
  padding: 8px;
}
.add-axis-cell {
  text-align: left !important;
  min-width: 200px;
}
.algorithm-label {
  text-align: left;
  padding: 8px;
  border: none;
  background-color: transparent !important;
}
.kdm-type-info {
  cursor: help;
}
.row-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.btn-delete-row {
  background: transparent;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 12px;
  opacity: 0.5;
}
.btn-delete-row:hover {
  opacity: 1;
}
</style>
